// ui/end-of-hand.js: pure helpers for the end-of-hand modal (M2.c-7).
//
// EndOfHandModal.jsx consumes these. Splitting them out keeps the JSX
// thin and lets Node tests cover the label/audio/timing logic without
// jsdom (same pattern as ui/bidding-logic.js per locked decision #17).
//
// Data contract, score_summary (matches plan-m2.md M2.c-7 + the shape
// state.lua's score_hand will populate into _public.score_summary):
//   {
//     result:           'made_it'|'march'|'loner_sweep'|'loner_partial'|'euchred',
//     scoring_team:     'team_a'|'team_b',
//     delta:            1|2|4,
//     prev_scores:      { team_a: number, team_b: number },
//     new_scores:       { team_a: number, team_b: number },
//     was_loner:        boolean,
//     maker_seat:       1|2|3|4,
//     tricks_per_seat:  { [seat]: count },
//   }

import { EVENTS } from '../audio/events.js';
import { isBower, _internals as cardInternals } from '../canvas/cards.js';

const { SUIT_SYMBOL, SUIT_NAME } = cardInternals;

// Per plan-m2.md M2.c-7 + DM2-11 (per-perspective labels). The strings
// here read from the maker's POV; the team palette (gold/teal) carries
// the "who actually got the points" channel.
const BREAKDOWN_LABEL = {
  made_it:       'Made it! +1',
  loner_partial: 'Made it alone! +1',
  march:         'Made the march! +2',
  loner_sweep:   'Loner sweep! +4',
  euchred:       'Euchred! Opponents +2',
};

const RECAP_TICK_MS  = 120;
const RECAP_TICKS    = 5;
const RECAP_TOTAL_MS = RECAP_TICK_MS * RECAP_TICKS;

/**
 * The short label rendered at the top of the modal summary.
 *
 * @param {{result?: string}} summary
 * @returns {string}
 */
export function formatBreakdownLabel(summary) {
  if (!summary || !summary.result) return '';
  return BREAKDOWN_LABEL[summary.result] || '';
}

/**
 * T12, perspective-correct headline. formatBreakdownLabel reads from
 * the maker's POV ('Made it! +1'), which is wrong half the time in
 * networked play: when THEY make it, the modal cheered at the viewer.
 * This variant resolves every result against the viewer's team:
 * 'They made it, THEM +1' / 'Euchred them! US +2'.
 *
 * Note the euchred wrinkle: scoring_team is the DEFENDING team, so
 * scoring_team === myTeam means WE euchred THEM.
 *
 * @param {{result?: string, scoring_team?: 'team_a'|'team_b', was_loner?: boolean}|null|undefined} summary
 * @param {'team_a'|'team_b'} myTeam
 * @returns {string}
 */
export function headlineFor(summary, myTeam) {
  if (!summary || !summary.result || !summary.scoring_team) return '';
  const us = summary.scoring_team === myTeam;
  const label = us ? 'US' : 'THEM';
  switch (summary.result) {
    case 'made_it':
      return us ? `We made it — ${label} +1` : `They made it — ${label} +1`;
    case 'loner_partial':
      return us ? `Made it alone — ${label} +1` : `They went alone and made it — ${label} +1`;
    case 'march':
      return us ? `March! All five tricks — ${label} +2` : `They marched — ${label} +2`;
    case 'loner_sweep':
      return us ? `LONER SWEEP! — ${label} +4` : `Their loner swept — ${label} +4`;
    case 'euchred':
      return us ? `Euchred them! ${label} +2` : `We got euchred — ${label} +2`;
    default:
      return '';
  }
}

/**
 * Per-team delta line. The display label uses "US"/"THEM" from the
 * single-player POV; team_a = US, team_b = THEM (matches the
 * scoreboard palette in scoreboard.js).
 *
 * @param {'team_a'|'team_b'} team
 * @param {number|undefined|null} prev
 * @param {number|undefined|null} next
 * @returns {string}
 */
export function formatScoreDelta(team, prev, next) {
  const label = team === 'team_a' ? 'US' : 'THEM';
  const p = (typeof prev === 'number' && Number.isFinite(prev)) ? prev : 0;
  const n = (typeof next === 'number' && Number.isFinite(next)) ? next : 0;
  return `${label}: ${p} → ${n}`;
}

/**
 * Format a single card for the partner reveal, rank + suit glyph plus
 * the bower suffix when applicable. Returns both a compact display
 * string and a longer label aimed at screen readers.
 *
 * @param {string} cardId
 * @param {string|null|undefined} trumpSuit
 * @returns {{ display: string, label: string }}
 */
export function formatRevealCard(cardId, trumpSuit) {
  if (!cardId || cardId.length !== 2) {
    return { display: '?', label: 'unknown card' };
  }
  const rank = cardId[0];
  const suit = cardId[1];
  const suitGlyph = SUIT_SYMBOL[suit] || '?';
  const suitWord  = SUIT_NAME[suit]   || 'unknown';
  const rankWord  = rank === 'T' ? 'Ten'
                  : rank === 'J' ? 'Jack'
                  : rank === 'Q' ? 'Queen'
                  : rank === 'K' ? 'King'
                  : rank === 'A' ? 'Ace'
                  : rank;
  const display = `${rank}${suitGlyph}`;
  const bower = isBower(cardId, trumpSuit);
  const suffix = bower === 'right' ? ' (right bower)'
               : bower === 'left'  ? ' (left bower)'
               : '';
  return { display, label: `${rankWord} of ${suitWord}${suffix}` };
}

/**
 * Format the partner's revealed hand. Returns the card tokens plus a
 * single aria-friendly sentence for screen readers.
 *
 * @param {string[] | undefined | null} hand
 * @param {string|null|undefined} trumpSuit
 * @returns {{ cards: Array<{display:string,label:string}>, ariaText: string }}
 */
export function formatPartnerHand(hand, trumpSuit) {
  const safe = Array.isArray(hand) ? hand : [];
  const cards = safe.map((id) => formatRevealCard(id, trumpSuit));
  const ariaText = cards.length
    ? `Partner held: ${cards.map((c) => c.label).join(', ')}.`
    : 'Partner had no cards to reveal.';
  return { cards, ariaText };
}

/**
 * Map a result/loner pair to its M5-bound audio event.
 *
 * M5 #9: the `euchred` branch splits by perspective. `scoring_team` in a
 * euchred summary is the defenders (since makers lost the hand). If our
 * team did the defending, we euchred them → EUCHRED_THEM. Otherwise we
 * were the makers who got beat → EUCHRED.
 *
 * `myTeam` defaults to 'team_a' for the hot-seat demo where the user is
 * always seat 1. Networked play passes the seat-derived team explicitly.
 *
 * @param {string|undefined|null} result
 * @param {boolean|undefined} was_loner
 * @param {'team_a'|'team_b'|undefined} [scoring_team]
 * @param {'team_a'|'team_b'|undefined} [myTeam]
 * @returns {string|null} EVENTS.X or null when result is unknown
 */
export function audioEventForResult(result, was_loner, scoring_team, myTeam) {
  const me = myTeam || 'team_a';
  switch (result) {
    case 'made_it':       return EVENTS.HAND_WON_PLUS1;
    case 'loner_partial': return EVENTS.HAND_WON_PLUS1;
    case 'march':         return was_loner ? EVENTS.LONER_SWEEP : EVENTS.HAND_WON_PLUS2;
    case 'loner_sweep':   return EVENTS.LONER_SWEEP;
    case 'euchred':       return scoring_team === me ? EVENTS.EUCHRED_THEM : EVENTS.EUCHRED;
    default:              return null;
  }
}

/**
 * Recap plan: how the per-trick highlight animation paces. With
 * prefers-reduced-motion the recap is instant, all 5 ticks revealed
 * at once and the summary appears on the same frame.
 *
 * @param {boolean} reduced_motion
 * @returns {{ tick_count: number, tick_ms: number, total_ms: number, instant: boolean }}
 */
export function computeRecapPlan(reduced_motion) {
  if (reduced_motion) {
    return { tick_count: RECAP_TICKS, tick_ms: 0, total_ms: 0, instant: true };
  }
  return {
    tick_count: RECAP_TICKS,
    tick_ms:    RECAP_TICK_MS,
    total_ms:   RECAP_TOTAL_MS,
    instant:    false,
  };
}

/**
 * Convert (elapsed_ms, plan) into the recap tick index a renderer
 * should display, 0 = no ticks shown, 5 = all ticks shown. Instant
 * plans always return 5 (summary is immediate).
 *
 * @param {number} elapsed_ms
 * @param {{ tick_count: number, tick_ms: number, instant: boolean }} plan
 * @returns {number} 0..plan.tick_count
 */
export function recapTickIndex(elapsed_ms, plan) {
  if (!plan || plan.instant) return plan ? plan.tick_count : 0;
  const t = Math.max(0, elapsed_ms);
  return Math.min(plan.tick_count, Math.floor(t / plan.tick_ms));
}

// M5 #15, fire orange used by the loner-sweep ceremony (matches
// particle-layer.js fire gradient base color).
const COLOR_FIRE = '#F0A030';

/**
 * Compute the chip-particle + screen-shake spec for a score outcome.
 * The end-of-hand modal calls this at recap-complete time and passes
 * the returned values to ParticleLayer / applyScreenShake.
 *
 * Tiering (plan-m5 §M5 #12 + #15):
 *   • +1 (made_it, loner_partial)  → 4 chip particles ('BURST'), no shake
 *   • +2 march (non-loner)         → 8 particles ('BURST'), 'march' shake
 *   • +2 euchre (defenders +2)     → 8 particles ('BURST'), no shake
 *   • +4 loner sweep / loner march → 8 fire-orange particles ('FIRE'),
 *     'loner' shake. (NetworkedGameView fires an additional FIRE burst
 *     at t=0ms of the 5th trick resolution.)
 *
 * Reduced-motion forces particleCount → 0 and shakeTier → 'none' per
 * the synthesis §2 contract. Audio is independent (handled elsewhere).
 *
 * `myTeam` chooses the particle color: gold (#F0C040) when our team
 * scored, teal (#3DC4C7) when theirs did. Loner sweeps override with
 * fire orange.
 *
 * @param {{
 *   result?: 'made_it'|'loner_partial'|'march'|'loner_sweep'|'euchred',
 *   was_loner?: boolean,
 *   delta?: number,
 *   scoring_team?: 'team_a'|'team_b',
 * }} summary
 * @param {'team_a'|'team_b'} myTeam
 * @param {boolean} reducedMotion
 * @returns {{
 *   particleCount: number,
 *   particleColor: string,
 *   particleType:  'BURST'|'FIRE',
 *   shakeTier:     'none'|'march'|'loner',
 * }}
 */
export function outcomeVisuals(summary, myTeam, reducedMotion) {
  const empty = { particleCount: 0, particleColor: '#F0C040', particleType: /** @type {const} */ ('BURST'), shakeTier: /** @type {const} */ ('none') };
  if (!summary || !summary.result) return empty;
  if (reducedMotion) return empty;

  const teamColor = (summary.scoring_team === myTeam) ? '#F0C040' : '#3DC4C7';

  // M5 #15, loner sweep is the only outcome that uses FIRE particles
  // + loner-tier shake. Both 'loner_sweep' and 'march' with was_loner
  // route through here (the maker took all 5 alone).
  if (summary.result === 'loner_sweep' || (summary.result === 'march' && summary.was_loner)) {
    return {
      particleCount: 20,
      particleColor: COLOR_FIRE,
      particleType: 'FIRE',
      shakeTier: 'loner',
    };
  }

  switch (summary.result) {
    case 'made_it':
    case 'loner_partial':
      return { particleCount: 4, particleColor: teamColor, particleType: 'BURST', shakeTier: 'none' };
    case 'march':
      return { particleCount: 14, particleColor: teamColor, particleType: 'BURST', shakeTier: 'march' };
    case 'euchred':
      return { particleCount: 8, particleColor: teamColor, particleType: 'BURST', shakeTier: 'none' };
    default:
      return empty;
  }
}

export const _internals = {
  BREAKDOWN_LABEL,
  RECAP_TICK_MS,
  RECAP_TICKS,
  RECAP_TOTAL_MS,
};

/** Live hands wait for HAND_SCORED and the last sweep. `skipWait` is
 * true only for a reattach whose snapshot was already at the hand end:
 * the server replays no HAND_SCORED, so that recap opens immediately. */
export function canShowRecap(handScoredEvent, sweepFrame, skipWait = false) {
  return skipWait || (handScoredEvent != null && sweepFrame == null);
}

/**
 * B3b D3: which team (A/B) won a trick, from the winner's lua seat.
 * Seats 1+3 are Team A, seats 2+4 are Team B.
 *
 * @param {number|null|undefined} winnerSeat
 * @returns {'A'|'B'|null}
 */
export function teamOfTrickWinner(winnerSeat) {
  if (winnerSeat === 1 || winnerSeat === 3) return 'A';
  if (winnerSeat === 2 || winnerSeat === 4) return 'B';
  return null;
}

/**
 * B3b D3: the accessible name for one recap box, e.g.
 * "Trick 3: us (Bot Caprica)". Unrevealed/unknown boxes name the
 * trick without a winner so every box still has a stable name.
 *
 * @param {number} index  0-based trick index
 * @param {number|null|undefined} winnerSeat
 * @param {string|null|undefined} winnerName
 * @returns {string}
 */
export function trickBoxAccessibleName(index, winnerSeat, winnerName, myTeam = 'team_a') {
  const n = index + 1;
  const team = teamOfTrickWinner(winnerSeat);
  if (winnerSeat == null || team == null) return `Trick ${n}: not decided yet`;
  const who = winnerName || `Seat ${winnerSeat}`;
  return `Trick ${n}: ${team === (myTeam === 'team_b' ? 'B' : 'A') ? 'us' : 'them'} (${who})`;
}

const ELLIPSIS = '\u2026';

/** The name with single spaces and no spaces at the ends. */
function recapCleanName(name) {
  return String(name || '').trim().replace(/\s+/g, ' ');
}

/**
 * The name without a leading "Bot ". The fallback "Bot 3" keeps it,
 * because "3" alone does not say who won.
 */
function recapBaseName(clean) {
  return clean.startsWith('Bot ') && !/^\d+$/.test(clean.slice(4)) ? clean.slice(4) : clean;
}

/**
 * The first word, plus the shortest start of the other words that tells
 * this name apart from rivals with the same first word ("Ada L.").
 *
 * @param {string} base
 * @param {string[]} others  the other distinct base names in the hand
 * @returns {{ head: string, tail: string }}
 */
function recapShortForm(base, others) {
  // The seat fallback "Bot 3" is one name, not a first word and more.
  if (/^Bot \d+$/.test(base)) return { head: base, tail: '' };
  const [first, ...more] = base.split(' ');
  const rest = more.join(' ');
  const rivals = others
    .filter((other) => other !== base && other.split(' ')[0] === first)
    .map((other) => other.split(' ').slice(1).join(' '));
  if (rivals.length === 0 || rest.length === 0) return { head: first, tail: '' };
  const letters = Array.from(rest);
  for (let n = 1; n <= letters.length; n += 1) {
    const start = letters.slice(0, n).join('');
    if (rivals.every((rival) => !rival.startsWith(start))) return { head: first, tail: ` ${start}.` };
  }
  return { head: first, tail: ` ${rest}` };
}

/**
 * Cut `head` (adding "…") until head + tail fits in maxPx. Drops the
 * tail only when even one letter of the head cannot fit beside it.
 */
function fitRecapText(head, tail, maxPx, measure) {
  if (measure(head + tail) <= maxPx) return head + tail;
  const letters = Array.from(head);
  for (const end of tail ? [tail, ''] : ['']) {
    for (let n = letters.length - 1; n >= 1; n -= 1) {
      const text = letters.slice(0, n).join('') + ELLIPSIS + end;
      if (measure(text) <= maxPx) return text;
    }
  }
  return ELLIPSIS;
}

/**
 * B3b item 1: the short names shown inside the five recap boxes. Each
 * label is measured and cut with "…" so it fits in maxPx; winners whose
 * names differ keep different labels ("Ada L." and "Ada B."). The full
 * name stays in each box's accessible name.
 *
 * @param {Array<string|null|undefined>} names  winner name per trick
 * @param {number} maxPx  room for the text inside one box
 * @param {(text: string) => number} measure  rendered width in px
 * @returns {string[]}  '' where there is no winner
 */
export function fitTrickWinnerLabels(names, maxPx, measure) {
  const cleans = names.map((name) => recapCleanName(name));
  const distinct = [...new Set(cleans.filter((clean) => clean.length > 0))];
  const bases = distinct.map((clean) => recapBaseName(clean));
  const distinctBases = [...new Set(bases)];
  /** @type {Map<string, string>} the label per clean full name */
  const labels = new Map();
  /** @type {Map<string, string[]>} texts to cut from, best first */
  const sources = new Map();
  distinct.forEach((clean, i) => {
    const { head, tail } = recapShortForm(bases[i], distinctBases);
    labels.set(clean, fitRecapText(head, tail, maxPx, measure));
    sources.set(clean, [...new Set([head + tail, bases[i], clean])]);
  });
  // Two different names can still end up with one label: "Bot Caprica"
  // and "Caprica" both drop to "Caprica", and "Christopher Smith" and
  // "Christophine Smith" both cut to "Christ…". Cut them again from a
  // longer source (the short form, the name without "Bot ", then the
  // full name), keeping more last letters or the part where they differ,
  // until every label in the hand is different.
  for (const clean of distinct) {
    const twins = distinct.filter((other) => labels.get(other) === labels.get(clean));
    if (twins.length < 2) continue;
    const taken = new Set(distinct.filter((other) => !twins.includes(other)).map((other) => labels.get(other)));
    const levels = Math.max(...twins.map((twin) => sources.get(twin)?.length ?? 0));
    let fixed = false;
    for (let level = 0; level < levels && !fixed; level += 1) {
      const texts = twins.map((twin) => {
        const list = sources.get(twin) ?? [twin];
        return Array.from(list[Math.min(level, list.length - 1)]);
      });
      const longest = Math.max(...texts.map((letters) => letters.length));
      /** @type {Array<(letters: string[], i: number) => string>} */
      const cuts = [];
      for (let keep = 0; keep < longest; keep += 1) {
        cuts.push((letters) => cutKeepingEnd(letters, keep, maxPx, measure));
      }
      cuts.push((letters, i) => cutAroundDifference(letters, texts.filter((_, j) => j !== i), maxPx, measure));
      for (const cut of cuts) {
        const tried = texts.map((letters, i) => cut(letters, i));
        const clash = tried.some((text) => text === ELLIPSIS || taken.has(text));
        if (!clash && new Set(tried).size === twins.length) {
          twins.forEach((twin, i) => labels.set(twin, tried[i]));
          fixed = true;
          break;
        }
      }
    }
    // Not fixed means even "a…x…" cannot fit the box, which no recap
    // width does. The labels then stay equal and the box's accessible
    // name and title still give each full name.
  }
  return cleans.map((clean) => (clean ? labels.get(clean) ?? '' : ''));
}

/**
 * The text whole when it fits, else its longest start that fits beside
 * "…" and its last `keep` letters ("Chris…r").
 *
 * @param {string[]} letters
 * @param {number} keep
 * @param {number} maxPx
 * @param {(text: string) => number} measure
 * @returns {string}  "…" when nothing fits
 */
function cutKeepingEnd(letters, keep, maxPx, measure) {
  const whole = letters.join('');
  if (measure(whole) <= maxPx) return whole;
  const end = letters.slice(letters.length - keep).join('');
  for (let n = letters.length - keep - 1; n >= 1; n -= 1) {
    const text = letters.slice(0, n).join('') + ELLIPSIS + end;
    if (measure(text) <= maxPx) return text;
  }
  return ELLIPSIS;
}

/**
 * A start, "…", then the letters from where this text first differs
 * from the rivals, then "…" if it does not reach the end ("Ab…x…").
 *
 * @param {string[]} letters
 * @param {string[][]} rivals
 * @param {number} maxPx
 * @param {(text: string) => number} measure
 * @returns {string}  "…" when nothing fits
 */
function cutAroundDifference(letters, rivals, maxPx, measure) {
  const differsAt = Math.min(...rivals.map((rival) => {
    let n = 0;
    while (n < letters.length && letters[n] === rival[n]) n += 1;
    return n;
  }));
  if (differsAt >= letters.length) return ELLIPSIS;
  for (let start = Math.min(differsAt, 3); start >= 1; start -= 1) {
    for (let n = letters.length - differsAt; n >= 1; n -= 1) {
      const middle = letters.slice(differsAt, differsAt + n).join('');
      const end = differsAt + n < letters.length ? ELLIPSIS : '';
      const text = letters.slice(0, start).join('') + (differsAt > start ? ELLIPSIS : '') + middle + end;
      if (measure(text) <= maxPx) return text;
    }
  }
  return ELLIPSIS;
}

/**
 * B2-5: the "Next hand in N s" countdown under the Continue button.
 * Counts down from the server's ready_deadline_server_ts using the same
 * clock-offset correction as TurnTimerRing. Never below zero.
 *
 * @param {number|null|undefined} deadlineMs  ready_deadline_server_ts
 * @param {number} nowMs  local clock (Date.now())
 * @param {number} [clockOffsetMs]  smoothed server-minus-client offset
 * @returns {string|null}  "Next hand in 23 s", or null without a deadline
 */
export function readyCountdownText(deadlineMs, nowMs, clockOffsetMs = 0) {
  if (deadlineMs == null) return null;
  const remaining = Math.max(0, deadlineMs - (nowMs + clockOffsetMs));
  return `Next hand in ${Math.ceil(remaining / 1000)} s`;
}

/**
 * Names of other connected human seats, in seat order. The client only
 * knows its own ready state, so callers may name one other player only.
 * Bots never block the advance (the server counts humans only), and a
 * bot-held human seat does not count either, so both are skipped. Falls
 * back to a seat code only when the snapshot carries no display name.
 *
 * Room occupants are matched by Lua seat (seat_index + 1, see
 * docs/m3-room-protocol.md): names need not be unique, so a display
 * name can never identify a seat. A seat with no room occupant at its
 * seat_index counts as not connected and is skipped: in a real game
 * every member carries a seat_index (see hooks/useRoomWs.js), so a
 * missing one means bad data, never a reason to name the seat.
 * Older room lists can omit seat_index on every occupant; only then
 * match unique display names and warn about duplicate-name limits.
 *
 * @param {Record<string|number, {display_name?: string, is_bot?: boolean, bot_held?: boolean}>|null|undefined} seatOccupants
 * @param {number|null|undefined} myLuaSeat
 * @param {Array<{display_name?: string, connection_status?: string, seat_index?: number|null}>|null} [roomOccupants]
 * @returns {string[]}
 */
export function pendingHumanNames(seatOccupants, myLuaSeat, roomOccupants = null) {
  if (!seatOccupants || typeof seatOccupants !== 'object') return [];
  const names = [];
  const namesOnly = Array.isArray(roomOccupants) && roomOccupants.length > 0
    && roomOccupants.every((occupant) => occupant?.seat_index == null);
  if (namesOnly) console.warn('[end-of-hand] room list has no seat_index; matching seats by display name, which is wrong for duplicate names');
  for (const seat of [1, 2, 3, 4]) {
    if (seat === myLuaSeat) continue;
    const occ = seatOccupants[seat] ?? seatOccupants[String(seat)];
    if (!occ || occ.is_bot || occ.bot_held) continue;
    if (roomOccupants) {
      // No room occupant at this seat's seat_index means not
      // connected: skip the seat.
      const match = roomOccupants.find((roomOcc) => namesOnly
        ? roomOcc?.display_name === occ.display_name
        : roomOcc?.seat_index === seat - 1);
      if (!match || match.connection_status === 'disconnected') continue;
    }
    names.push(occ.display_name || `Seat ${seat}`);
  }
  return names;
}

/**
 * After Continue, one connected human can be named because the hand
 * would already advance if that player had pressed. With several other
 * humans, the client cannot tell who has pressed.
 *
 * @param {Record<string|number, {display_name?: string, is_bot?: boolean, bot_held?: boolean}>|null|undefined} seatOccupants
 * @param {number|null|undefined} myLuaSeat
 * @param {Array<{display_name?: string, connection_status?: string, seat_index?: number|null}>|null} [roomOccupants]
 * @param {boolean} [pressed]
 * @returns {string|null}
 */
export function waitingForText(seatOccupants, myLuaSeat, roomOccupants = null, pressed = true) {
  if (!pressed) return null;
  const names = pendingHumanNames(seatOccupants, myLuaSeat, roomOccupants);
  if (names.length === 1) return `Waiting for ${names[0]}`;
  return 'Waiting for the others…';
}
